import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Link,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
} from '@mui/material';
import { Link as RouterLink } from 'react-router';
import { api } from '../api/client';
import { sectionById } from '../api/generated/section-catalog';
import { useCanEdit } from '../components/EditorOnly';
import { Stack, Typography } from '../components/mui-system';
import type {
  RegistryManagement,
  RegistryManagementState,
} from '../../../api/src/registry/registry-catalog.types';
import { RESOURCE_PLATFORMS } from '../../../api/src/registry/registry-platform';
import { useRegistryData } from './useRegistryData';
import {
  PlatformIcon,
  RegistryOwnershipChip,
  registryManagementLabels,
} from './RegistryCatalogTable';
import { RegistryResourceTable } from './RegistryResourceTable';
import { RegistryAiPlaceholder } from './RegistryAiPlaceholder';

function ManagementEditor({
  workspaceId,
  resourceKey,
  state,
  onSaved,
}: {
  workspaceId: string;
  resourceKey: string;
  state: RegistryManagementState;
  onSaved: () => void;
}) {
  const canEdit = useCanEdit('registry.edit');
  const [value, setValue] = useState(state.management);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const active = useRef(true);
  const saving = useRef(false);
  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);
  async function save() {
    if (!canEdit || saving.current) return;
    saving.current = true;
    setBusy(true);
    setError('');
    try {
      await api.saveRegistryManagement(workspaceId, resourceKey, value, state.revision);
      if (active.current) onSaved();
    } catch (e) {
      if (active.current)
        setError(e instanceof Error ? e.message : 'Не удалось сохранить управление');
    } finally {
      saving.current = false;
      if (active.current) setBusy(false);
    }
  }
  return (
    <Stack gap={1}>
      <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
        <TextField
          select
          size="small"
          label="Управление ресурсом"
          value={value}
          disabled={!canEdit || busy}
          sx={{ minWidth: 220 }}
          onChange={(e) => setValue(e.target.value as RegistryManagement)}
        >
          {Object.entries(registryManagementLabels).map(([id, label]) => (
            <MenuItem key={id} value={id}>
              {label}
            </MenuItem>
          ))}
        </TextField>
        {canEdit && (
          <Button disabled={busy || value === state.management} onClick={() => void save()}>
            {busy ? 'Сохранение…' : 'Сохранить управление'}
          </Button>
        )}
        {state.updatedAt && (
          <Typography variant="caption" color="text.secondary">
            Обновлено {new Date(state.updatedAt).toLocaleString('ru-RU')}
          </Typography>
        )}
      </Stack>
      <Typography variant="caption" color="text.secondary">
        Отметка команды о доступе к управлению. Принадлежность ресурса учитывается отдельно.
      </Typography>
      {error && <Alert severity="error">{error}</Alert>}
    </Stack>
  );
}

export function RegistryCatalogCard({
  workspaceId,
  resourceKey,
  onClose,
  onChanged,
}: {
  workspaceId: string;
  resourceKey: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [page, setPage] = useState(1);
  const request = useCallback(
    () => api.registryCatalogCard(workspaceId, resourceKey, page),
    [workspaceId, resourceKey, page],
  );
  const { data, error, loading, refresh } = useRegistryData(request);
  return (
    <Dialog open fullWidth maxWidth="lg" onClose={onClose}>
      <DialogTitle>
        <Stack direction="row" alignItems="center" gap={1}>
          {data && <PlatformIcon platform={data.resource.platform} />}
          {data?.resource.title ?? 'Ресурс реестра'}
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack gap={3}>
          {error && <Alert severity="error">{error}</Alert>}
          {loading && <CircularProgress size={24} aria-label="Загрузка ресурса" />}
          {data && (
            <>
              <Stack gap={1}>
                <Link
                  href={data.resource.url}
                  target="_blank"
                  rel="noreferrer"
                  sx={{ overflowWrap: 'anywhere' }}
                >
                  {data.resource.url}
                </Link>
                <Stack direction="row" gap={1} flexWrap="wrap">
                  <Chip
                    size="small"
                    label={RESOURCE_PLATFORMS[data.resource.platform].label}
                    variant="outlined"
                  />
                  <RegistryOwnershipChip value={data.resource.ownership} />
                  {data.resource.roles.includes('competitor') && (
                    <Chip size="small" label="Конкурент" />
                  )}
                </Stack>
                {data.resource.ownership === 'conflict' && (
                  <Alert severity="warning">
                    Есть противоречащие основания принадлежности. Проверьте исходные записи ниже.
                  </Alert>
                )}
                {data.resource.hasPageOwnership && (
                  <Typography variant="body2" color="text.secondary">
                    У отдельных URL есть собственные отметки принадлежности. Они не распространяются
                    на весь ресурс.
                  </Typography>
                )}
              </Stack>
              <ManagementEditor
                key={`${resourceKey}:${data.management.revision}`}
                workspaceId={workspaceId}
                resourceKey={resourceKey}
                state={data.management}
                onSaved={() => {
                  refresh();
                  onChanged();
                }}
              />
              <Divider />
              <Stack gap={1}>
                <Typography variant="h6">Записи и публикации · {data.referenceTotal}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {data.resource.donorCount
                    ? `Известных донорских URL: ${data.resource.donorCount}.`
                    : 'Донорских URL пока нет.'}{' '}
                  Точные ссылки и основания сохраняются в исходных разделах.
                </Typography>
                {data.references.length ? (
                  <RegistryResourceTable items={data.references} />
                ) : (
                  <Typography color="text.secondary">На этой странице записей нет.</Typography>
                )}
              </Stack>
              <Stack gap={1}>
                <Typography variant="h6">Поисковая выдача · {data.observationTotal}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Последние сохранённые снимки активных запросов «Карты выдачи». Повторный поиск не
                  запускается.
                </Typography>
                {data.observations.length ? (
                  <TableContainer>
                    <Table size="small" aria-label="Наблюдения в поисковой выдаче">
                      <TableHead>
                        <TableRow>
                          <TableCell>Запрос и условия</TableCell>
                          <TableCell>Позиция</TableCell>
                          <TableCell>Найденный URL</TableCell>
                          <TableCell>Дата</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.observations.map((row) => (
                          <TableRow key={row.id}>
                            <TableCell>
                              <Link
                                component={RouterLink}
                                to={
                                  row.runId
                                    ? `${sectionById('fakes').path}?run=${encodeURIComponent(row.runId)}`
                                    : `${sectionById('fakes').path}/${encodeURIComponent(row.keywordId)}`
                                }
                              >
                                {row.query}
                              </Link>
                              <Typography variant="caption" color="text.secondary" display="block">
                                {row.searchEngine === 'google' ? 'Google' : 'Яндекс'} ·{' '}
                                {row.regionName} · {row.device === 'desktop' ? 'Desktop' : 'Mobile'}
                              </Typography>
                            </TableCell>
                            <TableCell>Top-{row.rank}</TableCell>
                            <TableCell sx={{ maxWidth: 400, overflowWrap: 'anywhere' }}>
                              <Link href={row.url} target="_blank" rel="noreferrer">
                                {row.url}
                              </Link>
                            </TableCell>
                            <TableCell>
                              {new Date(row.capturedAt).toLocaleString('ru-RU')}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography color="text.secondary">
                    {data.observationTotal
                      ? 'На этой странице наблюдений нет.'
                      : 'В сохранённых снимках совпадений пока нет. Это не проверка наличия ресурса в индексе.'}
                  </Typography>
                )}
              </Stack>
              {Math.max(data.referenceTotal, data.observationTotal) > data.pageSize && (
                <TablePagination
                  component="div"
                  count={Math.max(data.referenceTotal, data.observationTotal)}
                  page={data.page - 1}
                  rowsPerPage={data.pageSize}
                  rowsPerPageOptions={[data.pageSize]}
                  onPageChange={(_, next) => setPage(next + 1)}
                />
              )}
              <RegistryAiPlaceholder compact />
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={refresh} disabled={loading}>
          Обновить карточку
        </Button>
        <Button onClick={onClose}>Закрыть</Button>
      </DialogActions>
    </Dialog>
  );
}
